/**
 * LoadingProvider.jsx — Middleware loading centralisé (Batch 0 FONDATION).
 *
 * SPEC-2026-06-07-loading-middleware.v1 — Design C.
 *
 * ## API hook useLoading()
 *   show(message?) -> token      Incrémente le compteur, retourne un token opaque
 *   hide(token)                  Décrémente idempotent (jamais < 0)
 *   setMessage(msg)              Remplace le message sans changer le compteur
 *   withLoading(promise, msg?)   show -> await -> hide en finally
 *   isLoading                    boolean (pending > 0 OR redux === true)
 *   forceHideAll()               Remet pending à 0 (pour 401 en dehors de React)
 *
 * ## Bridge Redux
 *   Le reducer loadingReducer stocke `isActive` initialisé avec `initEntityState(false)`
 *   (objet {loading, data, ...}) puis reçoit des boolean via setLoading(true/false).
 *   On normalise : actif UNIQUEMENT si === true (strict equality).
 *   active overlay = (pending > 0) OR (reduxLoading === true)
 *   Les ~40 dispatchers setLoading existants continuent de fonctionner sans modification.
 *
 * ## forceHideAll hors-React (apiCallWithToken.js)
 *   Un singleton `loadingHandle` est exporté depuis ce module.
 *   Le Provider enregistre sa fonction `forceHideAll` dans `loadingHandle.current`
 *   au mount. apiCallWithToken.js importe `loadingHandle` et appelle
 *   `loadingHandle.current?.()` sur 401.
 *   Choix documenté : on n'utilise pas un store redux dédié pour éviter de créer
 *   un 2ème canal de state. Le handle est nullable — si appelé
 *   avant le mount du Provider, il est no-op (try/catch dans apiCallWithToken).
 *
 * ## z-index
 *   z-index 10000 — au-dessus des pages et du header.
 *   SessionTimeoutModal (z-index ~9999 ou MUI backdrop 1300) reste cliquable car
 *   notre overlay est retiré du DOM quand inactif (conditional render).
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import { COLORS, RADII } from '../theme';
import * as selectors from '../../../store/selectors';

/* ─── Singleton handle pour forceHideAll hors-React ──────────────────────── */
/**
 * loadingHandle — ref module-level exportée.
 * apiCallWithToken.js l'importe et appelle `loadingHandle.current?.()` sur 401.
 * Le Provider enregistre la fonction reelle au mount.
 * @type {{ current: (() => void) | null }}
 */
export const loadingHandle = { current: null };

/* ─── Context ─────────────────────────────────────────────────────────────── */
const LoadingContext = createContext(null);

/* ─── Overlay Styled ──────────────────────────────────────────────────────── */
const spin = keyframes`to { transform: rotate(360deg); }`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(13, 10, 16, 0.65);
`;

const SpinnerRing = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border: 0.25rem solid rgba(194, 24, 166, 0.2);
  border-top-color: ${COLORS.accent};
  border-radius: ${RADII.circle};
  animation: ${spin} 0.85s linear infinite;
  margin-bottom: 0.75rem;
`;

const Message = styled.p`
  color: #fff;
  font-size: 0.9rem;
  margin: 0;
  text-align: center;
  padding: 0 1rem;
  max-width: 18rem;
`;

/* ─── Token counter ───────────────────────────────────────────────────────── */
let _tokenCounter = 0;
const genToken = () => {
  _tokenCounter += 1;
  return _tokenCounter;
};

/* ─── Provider ────────────────────────────────────────────────────────────── */
export function LoadingProvider({ children }) {
  const [pending, setPending] = useState(0);
  const [message, setMessage] = useState('');

  /* Bridge Redux : lire isActive depuis le store existant.
   * initEntityState(false) retourne {loading: true, data: false, ...} au boot ->
   * on normalise : actif UNIQUEMENT si === true (strict equality).           */
  const reduxIsActive = useSelector(selectors.loadingState);
  const reduxLoading = reduxIsActive === true;

  /* Overlay actif = compteur local OR redux bridge */
  const active = pending > 0 || reduxLoading;

  /* ── Debounce anti-flash navigation ──────────────────────────────────────
   * Chaque page dispatch setLoading(true) au montage pour son fetch ; sur réseau
   * rapide le chargement se résout en <300ms. Sans debounce, l'overlay sombre
   * (Backdrop rgba(13,10,16,.65)) apparaît+disparaît en une fraction de seconde
   * à CHAQUE navigation → "flash écran noir" très gênant.
   * Fix : on ne REND l'overlay qu'après 280ms d'activité continue. Les navs
   * rapides ne déclenchent jamais l'affichage (timer annulé au passage à false) ;
   * seules les opérations réellement longues montrent le loader (design validé). */
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!active) {
      setVisible(false);
      return undefined;
    }
    const id = setTimeout(() => setVisible(true), 280);
    return () => clearTimeout(id);
  }, [active]);

  /* forceHideAll — remet le compteur a 0 */
  const forceHideAll = useCallback(() => {
    setPending(0);
    setMessage('');
  }, []);

  /* Enregistrer forceHideAll dans le handle module-level au mount */
  useEffect(() => {
    loadingHandle.current = forceHideAll;
    return () => {
      loadingHandle.current = null;
    };
  }, [forceHideAll]);

  /* API : show */
  const show = useCallback((msg) => {
    const token = genToken();
    setPending((p) => p + 1);
    if (msg !== undefined) setMessage(msg);
    return token;
  }, []);

  /* API : hide — idempotent (token ne peut etre utilise qu'une seule fois) */
  const usedTokens = useRef(new Set());
  const hide = useCallback((token) => {
    if (token === undefined || token === null) return;
    if (usedTokens.current.has(token)) return;
    usedTokens.current.add(token);
    setPending((p) => Math.max(0, p - 1));
  }, []);

  /* API : withLoading */
  const withLoading = useCallback(
    async (promise, msg) => {
      const token = show(msg);
      try {
        return await promise;
      } finally {
        hide(token);
      }
    },
    [show, hide]
  );

  const isLoading = active;

  const contextValue = {
    show,
    hide,
    setMessage,
    withLoading,
    isLoading,
    forceHideAll,
  };

  return (
    <LoadingContext.Provider value={contextValue}>
      {children}
      {visible && (
        <Backdrop>
          <SpinnerRing />
          {message ? <Message>{message}</Message> : null}
        </Backdrop>
      )}
    </LoadingContext.Provider>
  );
}

/* ─── Hook ────────────────────────────────────────────────────────────────── */
/**
 * useLoading() — acces au middleware loading depuis tout composant React.
 * Doit etre appele dans un composant enfant du LoadingProvider.
 */
export function useLoading() {
  const ctx = useContext(LoadingContext);
  if (!ctx) {
    throw new Error('useLoading() must be used inside <LoadingProvider>.');
  }
  return ctx;
}
